"use client";

import { useRef } from "react";

/**
 * Returns a debounced version of the provided callback function.
 * The callback will only be executed after the specified delay has passed
 * since the last invocation.
 *
 * @param callback - The function to debounce
 * @param delayMs - Delay in milliseconds (default: 200)
 * @returns A debounced version of the callback
 *
 * @example
 * const debouncedSearch = useDebouncedCallback(
 *   async (query: string) => await searchApi(query),
 *   300
 * );
 * // Later: debouncedSearch("hello");
 */
export const useDebouncedCallback = <
  TProps extends any[] = any[],
  TReturn = void,
>(
  callback: (...props: TProps) => Promise<TReturn>,
  delayMs: number = 200,
): ((...props: TProps) => Promise<TReturn>) => {
  const timeout = useRef<NodeJS.Timeout | null>(null);

  return (...props: TProps) => {
    if (timeout.current) clearTimeout(timeout.current);
    return new Promise<TReturn>((resolve) => {
      timeout.current = setTimeout(() => resolve(callback(...props)), delayMs);
    });
  };
};
